import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, TextField, Button, Paper, Typography, CircularProgress,
  Chip, Grid, Pagination, ToggleButton, ToggleButtonGroup, Alert,
  Accordion, AccordionSummary, AccordionDetails, List, ListItem,
  ListItemText, Checkbox, Divider, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { searchHouseQuestions } from '../services/api';
import SearchSkeleton from './SearchSkeleton';
import EmptyState from './EmptyState';
import ErrorState from './ErrorState';
import DateHierarchyFilter from './DateHierarchyFilter';
import PageSizeSelector, { DEFAULT_PAGE_SIZE } from './PageSizeSelector';

// Reuse the Live Minutes teal — both surfaces live under "Chamber Records" and
// share the live-captures provenance, so visual kinship reinforces the link.
const THEME_COLOR = '#00897b';

// Chamber filter — Question Time runs in both the House and (rarely) the
// Federation Chamber. Values match the ES keyword exactly.
const CHAMBER_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'House', value: 'house' },
  { label: 'Federation Chamber', value: 'federation' },
];

const FACET_ORDER = ['askers', 'responders', 'portfolios', 'chambers'];

const FACET_LABELS = {
  askers: 'Asker',
  responders: 'Responder',
  portfolios: 'Portfolio',
  chambers: 'Chamber',
};

const CHAMBER_DISPLAY = {
  house: 'House',
  federation: 'Federation Chamber',
};

// Map a facet name to the API filter param name our search endpoint expects.
const FACET_TO_PARAM = {
  askers: 'asker',
  responders: 'responder',
  portfolios: 'portfolio',
  chambers: 'chamber',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

function formatStartTime(iso) {
  if (!iso) return null;
  // ES holds AEST naive ISO strings (e.g. 2026-05-25T14:00:42). Render the
  // clock time directly from the string to avoid the browser interpreting it
  // as UTC and shifting it by ~10h.
  const m = /T(\d{2}:\d{2})/.exec(iso);
  return m ? m[1] : null;
}

// Question snippet: prefer a highlight on `question_text` (the question is the
// headline thing for a QT row); fall back to the raw question_text trimmed to
// ~220 chars on a word boundary. If neither is available, fall back to the
// legacy full_text/transcript highlight so name-shaped queries that hit the
// chair's interjections still surface a fragment.
function questionSnippetHTMLFromHit(hit) {
  const hls = hit._highlights || {};
  const frags = (hls.question_text || []).slice(0, 2);
  if (frags.length) return frags.join(' … ');
  if (hit.question_text) {
    return null; // signal to caller: render plain question_text via snippetPlain()
  }
  // Legacy fallback — interjection-only matches with no question_text yet.
  const legacy = (hls.full_text || hls.transcript || []).slice(0, 2);
  if (legacy.length) return legacy.join(' … ');
  return null;
}

// Answer-match snippet: only set when the highlighter specifically hit the
// `answer_text` field — surfaced as a secondary "Answer match: …" line so the
// reader can see *why* a row matched when the question text alone didn't.
function answerSnippetHTMLFromHit(hit) {
  const hls = hit._highlights || {};
  const frags = (hls.answer_text || []).slice(0, 1);
  if (frags.length) return frags.join(' … ');
  return null;
}

function plainSnippet(text) {
  if (!text) return '';
  const trimmed = text.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= 220) return trimmed;
  const cut = trimmed.slice(0, 220);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 120 ? cut.slice(0, lastSpace) : cut) + '…';
}

export default function HouseQuestionsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [chamber, setChamber] = useState(searchParams.get('chamber') || '');
  const [activeFilters, setActiveFilters] = useState({
    asker: searchParams.get('asker') || null,
    responder: searchParams.get('responder') || null,
    portfolio: searchParams.get('portfolio') || null,
  });
  const [sort, setSort] = useState(searchParams.get('sort') || 'date_desc');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const urlPageSize = parseInt(searchParams.get('pageSize'), 10);
    return [10, 25, 50, 100].includes(urlPageSize) ? urlPageSize : DEFAULT_PAGE_SIZE;
  });
  const [facets, setFacets] = useState({});

  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    handleSearch(searchParams.get('q') || '*', 1, chamber, activeFilters, null, null, null, perPage, sort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (
    searchQuery = null,
    newPage = 1,
    chamberVal = chamber,
    filters = activeFilters,
    year = selectedYear,
    month = selectedMonth,
    day = selectedDay,
    pageSize = perPage,
    sortVal = sort,
  ) => {
    const q = searchQuery !== null ? searchQuery : (query || '*');
    setLoading(true);
    setError(null);
    try {
      const data = await searchHouseQuestions(
        q, newPage, pageSize,
        chamberVal || null,
        filters.asker || null,
        filters.responder || null,
        filters.portfolio || null,
        year, month, day,
        sortVal,
      );
      setResults(data);
      setPage(newPage);
      if (data.facets) setFacets(data.facets);
      const newParams = {};
      if (q && q !== '*') newParams.q = q;
      if (chamberVal) newParams.chamber = chamberVal;
      if (filters.asker) newParams.asker = filters.asker;
      if (filters.responder) newParams.responder = filters.responder;
      if (filters.portfolio) newParams.portfolio = filters.portfolio;
      if (pageSize !== DEFAULT_PAGE_SIZE) newParams.pageSize = pageSize;
      if (sortVal && sortVal !== 'date_desc') newParams.sort = sortVal;
      setSearchParams(newParams);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message || 'Failed to search. Please try again.');
      setResults(null);
    }
    setLoading(false);
    setInitialLoading(false);
  };

  const handleChamberChange = (event, newChamber) => {
    const value = newChamber === null ? '' : newChamber;
    setChamber(value);
    handleSearch(null, 1, value);
  };

  const handleFacetToggle = (facetName, value) => {
    const param = FACET_TO_PARAM[facetName];
    if (param === 'chamber') {
      // The chamber facet is already presented as a top-of-page toggle; in the
      // sidebar accordion it just mirrors that control.
      const next = chamber === value ? '' : value;
      setChamber(next);
      handleSearch(null, 1, next);
      return;
    }
    const next = { ...activeFilters };
    next[param] = next[param] === value ? null : value;
    setActiveFilters(next);
    handleSearch(null, 1, chamber, next);
  };

  const handleYearClick = (year) => {
    setSelectedYear(year);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, chamber, activeFilters, year, null, null);
  };

  const handleMonthClick = (month) => {
    const year = month.split('-')[0];
    setSelectedYear(year);
    setSelectedMonth(month);
    setSelectedDay(null);
    handleSearch(null, 1, chamber, activeFilters, year, month, null);
  };

  const handleDayClick = (day) => {
    const [yyyy, mm] = day.split('-');
    setSelectedYear(yyyy);
    setSelectedMonth(`${yyyy}-${mm}`);
    setSelectedDay(day);
    handleSearch(null, 1, chamber, activeFilters, yyyy, `${yyyy}-${mm}`, day);
  };

  const clearDateDrillDown = () => {
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    handleSearch(null, 1, chamber, activeFilters, null, null, null);
  };

  const clearAllFilters = () => {
    const cleared = { asker: null, responder: null, portfolio: null };
    setActiveFilters(cleared);
    handleSearch(null, 1, chamber, cleared);
  };

  const handlePageChange = (event, newPage) => {
    handleSearch(null, newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPerPage(newSize);
    handleSearch(null, 1, chamber, activeFilters, selectedYear, selectedMonth, selectedDay, newSize);
  };

  const handleSortChange = (event) => {
    const newSort = event.target.value;
    setSort(newSort);
    handleSearch(null, 1, chamber, activeFilters, selectedYear, selectedMonth, selectedDay, perPage, newSort);
  };

  const handleReset = () => {
    setQuery('');
    setChamber('');
    setActiveFilters({ asker: null, responder: null, portfolio: null });
    setResults(null);
    setPage(1);
    setFacets({});
    setSelectedYear(null);
    setSelectedMonth(null);
    setSelectedDay(null);
    setSort('date_desc');
    setSearchParams({});
    handleSearch('*', 1, '', { asker: null, responder: null, portfolio: null }, null, null, null, perPage, 'date_desc');
  };

  const hasDateFacets = facets.years?.length > 0 || facets.months?.length > 0 || facets.days?.length > 0;
  const hasAnyFacets = hasDateFacets
    || FACET_ORDER.some((name) => facets[name] && facets[name].length > 0);
  const hasActiveFilters = Object.values(activeFilters).some(Boolean) || selectedYear;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <QuestionAnswerIcon sx={{ color: THEME_COLOR, fontSize: 32 }} />
        <Typography variant="h4">House Questions (QT)</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Question Time questions captured live from HouseLive — each entry carries the asker, responder, portfolio, the asker's question, the responder's answer, and the full caption transcript with chair interjections.
      </Typography>

      {/* DRAFT/captions caveat — same provenance signal as Live Minutes. */}
      <Alert severity="info" variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        <strong>Live captions — not the official record.</strong> Transcripts are produced automatically from the HouseLive caption stream and may contain mis-hearings or missing speaker turns. The official record is Hansard.
      </Alert>

      <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="Search question transcripts, asker, responder…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            variant="outlined"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 }, minWidth: 240 }}
          />
          <Button
            variant="contained"
            onClick={() => handleSearch()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            sx={{ borderRadius: 3, px: 4, bgcolor: THEME_COLOR, '&:hover': { bgcolor: '#00695c' } }}
          >
            Search
          </Button>
          <Button variant="outlined" onClick={handleReset} sx={{ borderRadius: 3 }}>
            Reset
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={chamber}
            exclusive
            onChange={handleChamberChange}
            size="small"
            sx={{ flexWrap: 'wrap' }}
          >
            {CHAMBER_OPTIONS.map((opt) => (
              <ToggleButton
                key={opt.value || 'all'}
                value={opt.value}
                sx={{
                  textTransform: 'none',
                  '&.Mui-selected': { bgcolor: THEME_COLOR, color: 'white', '&:hover': { bgcolor: '#00695c' } }
                }}
              >
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {initialLoading && <SearchSkeleton count={6} showFacets={true} />}

      {!initialLoading && error && (
        <ErrorState message={error} onRetry={() => handleSearch()} themeColor={THEME_COLOR} />
      )}

      {!initialLoading && !error && results && results.total === 0 && (
        <EmptyState
          query={query}
          type="house questions"
          onSuggestionClick={(q) => { setQuery(q); handleSearch(q); }}
          onReset={handleReset}
          themeColor={THEME_COLOR}
        />
      )}

      {!initialLoading && !error && results && results.total > 0 && (
        <Grid container spacing={3}>
          {hasAnyFacets && (
            <Grid item xs={12} md={3}>
              <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>Filter Results</Typography>
                  {hasActiveFilters && (
                    <Button size="small" onClick={() => { clearAllFilters(); clearDateDrillDown(); }}>Clear All</Button>
                  )}
                </Box>

                {hasDateFacets && (
                  <DateHierarchyFilter
                    facets={facets}
                    selectedYear={selectedYear}
                    selectedMonth={selectedMonth}
                    selectedDay={selectedDay}
                    onYearClick={handleYearClick}
                    onMonthClick={handleMonthClick}
                    onDayClick={handleDayClick}
                    onClearAll={clearDateDrillDown}
                    themeColor={THEME_COLOR}
                  />
                )}

                {FACET_ORDER.map((facetName) => (
                  facets[facetName] && facets[facetName].length > 0 && (
                    <Accordion key={facetName} defaultExpanded={['askers', 'responders'].includes(facetName)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">{FACET_LABELS[facetName]}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <List dense sx={{ py: 0 }}>
                          {facets[facetName].slice(0, 25).map((bucket) => {
                            const param = FACET_TO_PARAM[facetName];
                            const isActive = param === 'chamber'
                              ? chamber === bucket.value
                              : activeFilters[param] === bucket.value;
                            const display = facetName === 'chambers'
                              ? (CHAMBER_DISPLAY[bucket.value] || bucket.value)
                              : bucket.value;
                            return (
                              <ListItem key={String(bucket.value)} disablePadding>
                                <Checkbox
                                  size="small"
                                  checked={isActive}
                                  onChange={() => handleFacetToggle(facetName, bucket.value)}
                                  sx={{ color: THEME_COLOR, '&.Mui-checked': { color: THEME_COLOR } }}
                                />
                                <ListItemText
                                  primary={`${display} (${bucket.count.toLocaleString()})`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  )
                ))}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={hasAnyFacets ? 9 : 12}>
            <Paper elevation={1} sx={{ p: 2, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle1">
                    {results.total.toLocaleString()} {results.total === 1 ? 'question' : 'questions'} found
                  </Typography>
                  <PageSizeSelector value={perPage} onChange={handlePageSizeChange} />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="house-questions-sort-label">Sort by</InputLabel>
                    <Select
                      labelId="house-questions-sort-label"
                      value={sort}
                      label="Sort by"
                      onChange={handleSortChange}
                    >
                      <MenuItem value="date_desc">Newest first</MenuItem>
                      <MenuItem value="date_asc">Oldest first</MenuItem>
                      <MenuItem value="_score">Relevance</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {results.total > perPage && (
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>

              <List>
                {results.hits.map((hit, idx) => {
                  const date = formatDate(hit.date);
                  const time = formatStartTime(hit.start_time);
                  const askerName = hit.asker?.name || 'Unknown';
                  const responderName = hit.responder?.name || 'Unknown';
                  const portfolioName = hit.responder?.portfolio;
                  const chamberDisplay = CHAMBER_DISPLAY[hit.chamber] || hit.chamber;
                  // Question line — the headline thing for a QT row.
                  const questionHTML = questionSnippetHTMLFromHit(hit);
                  const questionPlain = questionHTML ? null : plainSnippet(hit.question_text);
                  // Answer-match line — only when the highlighter specifically
                  // landed inside the answer (i.e. the row matched on
                  // answer_text). Shown below the question, prefixed
                  // "Answer match:" so the reader can see what tripped the hit.
                  const answerHTML = answerSnippetHTMLFromHit(hit);
                  return (
                    <React.Fragment key={hit.id}>
                      {idx > 0 && <Divider />}
                      <ListItem alignItems="flex-start" sx={{ flexDirection: 'column', py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Typography
                            variant="resultTitle"
                            component={RouterLink}
                            to={`/house-questions/${encodeURIComponent(hit.id)}`}
                            sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            Q{hit.question_number ?? '?'} — {askerName} to {responderName}
                            {portfolioName ? ` (${portfolioName})` : ''}
                          </Typography>
                          <Chip label={chamberDisplay} size="small" variant="outlined" />
                          {date && (
                            <Chip label={date} size="small" variant="outlined" />
                          )}
                          {time && (
                            <Chip label={time} size="small" variant="outlined" />
                          )}
                        </Box>
                        {hit.raw_line && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            {hit.raw_line}
                          </Typography>
                        )}
                        {questionHTML ? (
                          <Typography
                            className="ps-snippet"
                            variant="body2"
                            color="text.secondary"
                            dangerouslySetInnerHTML={{ __html: questionHTML }}
                          />
                        ) : questionPlain ? (
                          <Typography className="ps-snippet" variant="body2" color="text.secondary">
                            {questionPlain}
                          </Typography>
                        ) : null}
                        {answerHTML && (
                          <Typography
                            className="ps-snippet"
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 0.5 }}>
                              Answer match:
                            </Box>
                            <Box
                              component="span"
                              dangerouslySetInnerHTML={{ __html: answerHTML }}
                            />
                          </Typography>
                        )}
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>

              {results.total > perPage && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(results.total / perPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
